import { DAG, DAGResolver } from '@lc/libdag';
import { convertLatLngBoundsToLngLatBoundsLike, convertLngLatBoundsToLatLngBounds, } from './convert.js';
import { propEvent } from './events/prop.js';
export class MaplibreInstance {
    #map;
    #objects = new Map();
    #ready = false;
    #readyListeners = new Set();
    #images = new Map();
    #layers = new Map();
    constructor(map, layers) {
        for (const [layer, def] of Object.entries(layers)) {
            this.#layers.set(layer, def);
        }
        this.#map = map;
        this.#objects = new Map();
        void map.once('load', () => {
            for (const image of map.listImages()) {
                let key = image;
                let pixelRatio = 1;
                const match = image.match(/^(.+)@(\d+)$/);
                if (match) {
                    key = match[1];
                    pixelRatio = +match[2];
                }
                this.#images.set('#' + image, new Map([[pixelRatio, key]]));
            }
            this.#setLayers();
            this.#ready = true;
            for (const fn of this.#readyListeners) {
                queueMicrotask(fn);
            }
            this.#readyListeners.clear();
        });
    }
    get attached() {
        if (!this.#map)
            return null;
        return this.#map._container;
    }
    get rawMap() {
        return this.#map;
    }
    get ready() {
        return this.#ready;
    }
    _ensure() {
        if (!this.#map)
            throw new Error('No map available');
        if (!this.ready)
            throw new Error('Map not ready');
        return this.#map;
    }
    onReady(callback) {
        if (this.#ready)
            return queueMicrotask(callback);
        this.#readyListeners.add(callback);
    }
    destroy() {
        if (!this.#map)
            return;
        this.#map.remove();
        this.#map = null;
        this.#objects.clear();
    }
    getZoom() {
        return this._ensure().getZoom();
    }
    getBounds() {
        return convertLngLatBoundsToLatLngBounds(this._ensure().getBounds());
    }
    getPixelRatio() {
        return this._ensure().getPixelRatio();
    }
    getPadding() {
        const { top, left, bottom, right } = this._ensure().getPadding();
        return {
            top: top ?? 0,
            left: left ?? 0,
            bottom: bottom ?? 0,
            right: right ?? 0,
        };
    }
    setPadding(padding) {
        this._ensure().setPadding(padding);
    }
    getMinZoom() {
        return this._ensure().getMinZoom();
    }
    getMaxZoom() {
        return this._ensure().getMaxZoom();
    }
    getMaxBounds() {
        const bounds = this._ensure().getMaxBounds();
        if (!bounds)
            return null;
        return convertLngLatBoundsToLatLngBounds(bounds);
    }
    setMinZoom(minZoom) {
        this._ensure().setMinZoom(minZoom);
    }
    setMaxZoom(maxZoom) {
        this._ensure().setMaxZoom(maxZoom);
    }
    setMaxBounds(maxBounds) {
        this._ensure().setMaxBounds(maxBounds ? convertLatLngBoundsToLngLatBoundsLike(maxBounds) : null);
    }
    setOptions(options) {
        const map = this._ensure();
        if (options.layers) {
            this.#layers.clear();
            for (const [layer, def] of Object.entries(options.layers)) {
                this.#layers.set(layer, def);
            }
        }
        if (options.style) {
            map.setStyle(options.style, { diff: true });
            this.#setLayers();
        }
    }
    #setLayers() {
        if (!this.#map)
            throw new Error('Map is destroyed');
        const layerDag = new DAG();
        this.#map.addLayer({
            id: '#top',
            type: 'symbol',
            source: {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] },
            },
        });
        for (const layer of this.#map.getLayersOrder())
            layerDag.addItem(layer);
        for (const layer of this.#layers.keys())
            layerDag.addItem(layer);
        for (const [layer, { under }] of this.#layers.entries()) {
            layerDag.addDep(layer, under);
        }
        for (const layer of new DAGResolver(layerDag).getSorted()) {
            const def = this.#layers.get(layer);
            if (!def)
                continue;
            this.#map.addLayer({
                id: layer,
                type: 'symbol',
                source: {
                    type: 'geojson',
                    data: { type: 'FeatureCollection', features: [] },
                },
            }, def.under);
        }
    }
    get _images() {
        return this.#images;
    }
    get _objects() {
        return this.#objects;
    }
    _addObject(obj) {
        this.#objects.set(obj.id, obj);
        return obj;
    }
    get _layers() {
        return this.#layers;
    }
    static check(instance) {
        if (!(instance instanceof MaplibreInstance)) {
            throw new TypeError(`Need an instance of MaplibreInstance, from libmap-maplibre-gl`);
        }
        if (!instance.rawMap)
            throw new Error(`Map has been destroyed`);
    }
    static isPropEvent(type) {
        return type === 'pixelRatio';
    }
    _attachPropEvent(type, handler) {
        switch (type) {
            case 'pixelRatio':
                return this._attachPixelRatioEvent(handler);
            default:
                throw new Error(`Unknown propEvent: ${type}`);
        }
    }
    _attachPixelRatioEvent(handler) {
        const controller = new AbortController();
        const doHandle = () => {
            if (controller.signal.aborted)
                return;
            const window = this._ensure()._container.ownerDocument.defaultView;
            if (!window)
                throw new Error('Cannot get window for map');
            const handleOnce = () => {
                window
                    .matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`)
                    .addEventListener('change', () => {
                    handleOnce();
                    setTimeout(() => handler(propEvent('pixelRatio', this.getPixelRatio())), 0);
                }, {
                    once: true,
                    signal: controller.signal,
                });
            };
            handleOnce();
        };
        if (this.#ready)
            doHandle();
        else
            this.onReady(doHandle);
        return () => controller.abort();
    }
}
//# sourceMappingURL=data:application/json;base64,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